import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  QrCode as QrCodeIcon,
  Badge as BadgeIcon,
  CameraAlt as ScanIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { QrBadgesMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';

export interface QrBadgesMetricsSectionProps {
  data: QrBadgesMetrics;
  loading?: boolean;
}

const QrBadgesMetricsSection: React.FC<QrBadgesMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const scanTypeData = useMemo(
    () =>
      data.scansByType.map((item, index) => ({
        name: item.type,
        value: item.count,
        color: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.scansByType]
  );

  const hasScanTypeData = scanTypeData.length > 0;

  return (
    <Box>
      <SectionHeader title="QR Codes & Badges" icon={<QrCodeIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total QR Scans"
            value={data.totalQrScans}
            icon={<QrCodeIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Badges"
            value={data.activeBadges}
            icon={<BadgeIcon />}
            color={CHART_COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Badge Scans"
            value={data.totalBadgeScans}
            icon={<ScanIcon />}
            color={CHART_COLORS.purple}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Most Scanned Users"
            value={data.mostScannedUsers.length}
            icon={<BadgeIcon />}
            color={CHART_COLORS.cyan}
            loading={loading}
          />
        </Grid>

        {/* Charts */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Scans by Type"
            subtitle="Distribution of scan types"
            height={280}
            loading={loading}
          >
            {!hasScanTypeData ? (
              <EmptyState
                message="No scan type data available"
                icon={<QrCodeIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={scanTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {scanTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {scanTypeData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Most Scanned Users"
            subtitle="Users with highest badge scans"
            height={280}
            loading={loading}
          >
            {data.mostScannedUsers.length === 0 ? (
              <EmptyState
                message="No scan data available"
                icon={<BadgeIcon sx={{ fontSize: 32, color: '#9ca3af' }} />}
                height={250}
              />
            ) : (
              <Box sx={{ p: 1 }}>
                {data.mostScannedUsers.slice(0, 5).map((user, index) => (
                  <Box
                    key={user.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      mb: 1,
                      bgcolor: '#f9fafb',
                      borderRadius: 2,
                      '&:hover': { bgcolor: '#f3f4f6' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor:
                            index === 0
                              ? '#fbbf24'
                              : index === 1
                                ? '#9ca3af'
                                : index === 2
                                  ? '#cd7f32'
                                  : '#e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#111827',
                        }}
                      >
                        {user.userName}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: CHART_COLORS.primary,
                        bgcolor: '#dbeafe',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {user.scanCount} scans
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QrBadgesMetricsSection;
